<script setup>
import { computed } from 'vue';
import TemplatePickersToolbar from '../TemplatePickers/TemplatePickersToolbar.vue';

defineOptions({ name: 'ConditionBuilder' });

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ combinator: 'all', rules: [] }),
  },
  apiSample: { type: [Object, Array, String, Number, Boolean], default: null },
  allSlots: { type: Array, default: () => [] },
  agentBotId: { type: Number, default: null },
  excludeSlotId: { type: [Number, String], default: null },
  depth: { type: Number, default: 0 },
});

const emit = defineEmits(['update:modelValue']);

const SOURCES = [
  { value: 'api', label: 'Resposta API', icon: 'i-lucide-globe' },
  { value: 'field', label: 'Field', icon: 'i-lucide-tag' },
  { value: 'contact', label: 'Contato', icon: 'i-lucide-user' },
  { value: 'slot', label: 'Slot', icon: 'i-lucide-layers' },
];

const OPERATORS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '≠' },
  { value: 'contains', label: 'contém' },
  { value: 'not_contains', label: 'não contém' },
  { value: 'starts_with', label: 'começa com' },
  { value: 'ends_with', label: 'termina com' },
  { value: 'regex', label: 'regex' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'between', label: 'entre' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'exists', label: 'existe' },
  { value: 'not_exists', label: 'não existe' },
  { value: 'is_empty', label: 'vazio' },
  { value: 'is_not_empty', label: 'não vazio' },
];

const VALUE_LESS_OPERATORS = ['exists', 'not_exists', 'is_empty', 'is_not_empty'];
const LIST_OPERATORS = ['in', 'not_in', 'between'];

const conditions = computed({
  get: () => props.modelValue || { combinator: 'all', rules: [] },
  set: value => emit('update:modelValue', value),
});

const updateCombinator = value => {
  conditions.value = { ...conditions.value, combinator: value };
};

const addRule = () => {
  const rules = [...(conditions.value.rules || []), {
    source: 'api', field: '', operator: 'equals', value: '',
  }];
  conditions.value = { ...conditions.value, rules };
};

const addGroup = () => {
  const rules = [...(conditions.value.rules || []), {
    group: { combinator: 'all', rules: [] },
  }];
  conditions.value = { ...conditions.value, rules };
};

const removeRule = idx => {
  const rules = [...(conditions.value.rules || [])];
  rules.splice(idx, 1);
  conditions.value = { ...conditions.value, rules };
};

const updateRule = (idx, partial) => {
  const rules = [...(conditions.value.rules || [])];
  rules[idx] = { ...rules[idx], ...partial };
  conditions.value = { ...conditions.value, rules };
};

const updateGroup = (idx, value) => {
  const rules = [...(conditions.value.rules || [])];
  rules[idx] = { group: value };
  conditions.value = { ...conditions.value, rules };
};

const stringifyValue = value => {
  if (Array.isArray(value)) return value.join(', ');
  return value ?? '';
};

const parseValue = (operator, raw) => {
  if (LIST_OPERATORS.includes(operator)) {
    if (Array.isArray(raw)) return raw;
    return String(raw || '').split(',').map(s => s.trim()).filter(Boolean);
  }
  return raw;
};

const handleFieldPick = (idx, payload) => {
  const rule = conditions.value.rules[idx];
  const token = payload.token.replace(/^{{|}}$/g, '');
  const [source, ...rest] = token.split('.');
  updateRule(idx, {
    source: SOURCES.some(s => s.value === source) ? source : rule.source,
    field: rest.join('.'),
  });
};

const handleValuePick = (idx, payload) => {
  const rule = conditions.value.rules[idx];
  const current = rule.value ?? '';
  updateRule(idx, { value: `${current}${payload.token}` });
};
</script>

<template>
  <div
    class="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 space-y-2"
    :class="{ 'ml-4': depth > 0 }"
  >
    <div class="flex items-center gap-2">
      <span class="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {{ $t('OMNI.SLOTS.API_AUTOMATIONS.COMBINATOR', 'Combinador') }}
      </span>
      <div class="inline-flex rounded overflow-hidden border border-slate-300 dark:border-slate-600 text-xs">
        <button
          type="button"
          class="px-2 py-0.5 font-semibold"
          :class="conditions.combinator === 'all'
            ? 'bg-woot-500 text-white'
            : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300'"
          @click="updateCombinator('all')"
        >
          TODAS (E)
        </button>
        <button
          type="button"
          class="px-2 py-0.5 font-semibold"
          :class="conditions.combinator === 'any'
            ? 'bg-woot-500 text-white'
            : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300'"
          @click="updateCombinator('any')"
        >
          QUALQUER (OU)
        </button>
      </div>
    </div>

    <div
      v-if="!conditions.rules || !conditions.rules.length"
      class="text-xs text-slate-500 italic"
    >
      {{ $t('OMNI.SLOTS.API_AUTOMATIONS.NO_RULES', 'Sem regras — a automação dispara sempre.') }}
    </div>

    <div
      v-for="(rule, idx) in conditions.rules"
      :key="idx"
      class="group"
    >
      <!-- Nested group -->
      <div
        v-if="rule.group"
        class="relative"
      >
        <ConditionBuilder
          :model-value="rule.group"
          :api-sample="apiSample"
          :all-slots="allSlots"
          :agent-bot-id="agentBotId"
          :exclude-slot-id="excludeSlotId"
          :depth="depth + 1"
          @update:model-value="updateGroup(idx, $event)"
        />
        <button
          type="button"
          class="absolute top-1 right-1 p-1 text-slate-400 hover:text-red-500"
          @click="removeRule(idx)"
        >
          <span class="i-lucide-x w-3 h-3" />
        </button>
      </div>

      <!-- Simple rule -->
      <div
        v-else
        class="flex flex-wrap items-start gap-1 text-xs bg-white dark:bg-slate-700/60 rounded border border-slate-200 dark:border-slate-600 p-2"
      >
        <select
          :value="rule.source || 'api'"
          class="px-1 py-0.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-xs"
          @change="updateRule(idx, { source: $event.target.value, field: '' })"
        >
          <option
            v-for="s in SOURCES"
            :key="s.value"
            :value="s.value"
          >
            {{ s.label }}
          </option>
        </select>

        <div class="flex items-center gap-1 flex-1 min-w-[180px]">
          <input
            :value="rule.field"
            :placeholder="rule.source === 'api' ? 'data.status' : 'nome_do_campo'"
            class="flex-1 px-2 py-0.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 font-mono text-xs"
            @input="updateRule(idx, { field: $event.target.value })"
          />
          <TemplatePickersToolbar
            :agent-bot-id="agentBotId"
            :exclude-slot-id="excludeSlotId"
            :all-slots="allSlots"
            :api-sample="apiSample"
            label="Escolher"
            @pick="handleFieldPick(idx, $event)"
          />
        </div>

        <select
          :value="rule.operator || 'equals'"
          class="px-1 py-0.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-xs"
          @change="updateRule(idx, { operator: $event.target.value })"
        >
          <option
            v-for="op in OPERATORS"
            :key="op.value"
            :value="op.value"
          >
            {{ op.label }}
          </option>
        </select>

        <div
          v-if="!VALUE_LESS_OPERATORS.includes(rule.operator)"
          class="flex items-center gap-1 flex-1 min-w-[160px]"
        >
          <input
            :value="stringifyValue(rule.value)"
            :placeholder="LIST_OPERATORS.includes(rule.operator) ? 'v1, v2, v3' : 'valor'"
            class="flex-1 px-2 py-0.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-xs"
            @input="updateRule(idx, { value: parseValue(rule.operator, $event.target.value) })"
          />
          <TemplatePickersToolbar
            :agent-bot-id="agentBotId"
            :exclude-slot-id="excludeSlotId"
            :all-slots="allSlots"
            :api-sample="apiSample"
            label="{{ }}"
            @pick="handleValuePick(idx, $event)"
          />
        </div>

        <button
          type="button"
          class="p-1 text-slate-400 hover:text-red-500"
          @click="removeRule(idx)"
        >
          <span class="i-lucide-trash-2 w-3 h-3" />
        </button>
      </div>
    </div>

    <div class="flex gap-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
        @click="addRule"
      >
        <span class="i-lucide-plus w-3 h-3" /> {{ $t('OMNI.SLOTS.API_AUTOMATIONS.ADD_RULE', 'Regra') }}
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600"
        @click="addGroup"
      >
        <span class="i-lucide-folder-plus w-3 h-3" /> {{ $t('OMNI.SLOTS.API_AUTOMATIONS.ADD_GROUP', 'Grupo') }}
      </button>
    </div>
  </div>
</template>
